import { useEffect, useRef, useState } from "react";
import { TenantSSOError } from "@madhive/mad-sdk";
import { useMadSdk } from "../../../hooks/madSDK";
const LOOKUP_DEBOUNCE_MS = 400;
/**
 * Hook for signing in through an organization's own identity provider.
 * Looks up the provider once typing pauses, and signs in with it in a popup.
 * @param emailOrDomain the typed email, or a bare email domain; an email is looked
 * up once there is text after the `@`, and a bare domain once it has a `.`.
 * @param verify rejects a user the caller will not admit.
 * @param authDomain the host serving the IdP handler; the project default when omitted.
 */
export function useTenantSSO({ emailOrDomain, verify, authDomain }) {
    const madSDK = useMadSdk();
    const [provider, setProvider] = useState(null);
    const [lookupFailed, setLookupFailed] = useState(false);
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [error, setError] = useState();
    // A newer sign-in replaces an older one's popup; only the newest reports.
    const attempt = useRef(0);
    useEffect(() => {
        setProvider(null);
        setLookupFailed(false);
        setError(undefined);
        const value = emailOrDomain.trim();
        const at = value.lastIndexOf("@");
        if (at < 0 ? !value.includes(".") : at === value.length - 1) {
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(() => {
            madSDK.authentication.tenantSSO
                .discover(value)
                .then((found) => {
                if (!cancelled) {
                    setProvider(found);
                }
            })
                .catch(() => {
                if (!cancelled) {
                    setLookupFailed(true);
                }
            });
        }, LOOKUP_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [emailOrDomain, madSDK]);
    /**
     * Signs in with the looked-up provider. Call it straight from a click:
     * browsers block popups opened without a user gesture.
     * @returns the signed-in account, or undefined when it did not finish: see
     * `error`, which stays unset when the user closed the popup.
     */
    async function signIn() {
        if (!provider) {
            return undefined;
        }
        attempt.current += 1;
        const mine = attempt.current;
        setError(undefined);
        setIsSigningIn(true);
        try {
            return await madSDK.authentication.tenantSSO.signIn(provider, { authDomain, verify });
        }
        catch (e) {
            const reason = e instanceof TenantSSOError ? e.reason : "FAILED";
            if (mine === attempt.current && reason !== "CANCELLED" && reason !== "SUPERSEDED") {
                setError(reason === "LOOKUP_FAILED" ? "FAILED" : reason);
            }
            return undefined;
        }
        finally {
            if (mine === attempt.current) {
                setIsSigningIn(false);
            }
        }
    }
    let status = provider ? "ready" : "idle";
    if (error) {
        status = "error";
    }
    if (isSigningIn) {
        status = "processing";
    }
    return { provider, lookupFailed, status, error, signIn };
}
//# sourceMappingURL=index.js.map